/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { get } from '@ember/object';
import Component from '@glimmer/component';
import type { ContentValue } from '@glint/template';

import type { IBvTableColumn, IBvTableRow } from 'volta/components/bv-table';
import type { BvTooltipSignature } from 'volta/components/bv-tooltip';

export interface CellBaseCellSignature<M = any, V = any> {
  Args: {
    cell: any;
    row: IBvTableRow;
    column: IBvTableColumn;
    isFooter?: boolean;
    scrollToRow: (rowModelId: string) => void;
  };
  Blocks: {
    default: [IBvTableBaseCellApi<M, V>];
  };
  Element: HTMLDivElement;
}

export interface IBvTableBaseCellValue<T = any> {
  cellClasses?: string;
  onClick?: (api: IBvTableBaseCellApi<T>) => void;
  tooltip?: string;
  tooltipSubtext?: string;
  tooltipContent?: BvTooltipSignature['Args']['content'];
}

export interface IBvTableBaseCellApi<T = any, V = any> {
  cell: any;
  model: T;
  value: V;
  column: IBvTableColumn;
  isSelected: boolean;
  scrollToRow: (rowModelId: string) => void;
}

export default class CellBaseCellComponent extends Component<CellBaseCellSignature> {
  get cell() {
    return this.args.cell;
  }

  get model() {
    return this.args.row.api.rowValue;
  }

  get isSelected() {
    return Boolean(this.args.row.api.rowMeta?.isSelected);
  }

  get valueAsFormat() {
    return this.value as IBvTableBaseCellValue;
  }

  get valueAsContentValue() {
    return this.value as ContentValue;
  }

  get value(): any {
    const { format, footerFormat, valuePath } = this.args.column;

    if (this.args.isFooter && typeof footerFormat === 'function') {
      return footerFormat(this.model, this.args.row);
    }

    if (!this.args.isFooter && typeof format === 'function') {
      return format(this.model, this.args.row);
    }

    return get(this.model, valuePath) as ContentValue;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Cell::BaseCell': typeof CellBaseCellComponent;
    'cell/base-cell': typeof CellBaseCellComponent;
  }
}
